import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "468",
  "slug": "what-is-the-water-cycle",
  "title": "What Is the Water Cycle? How Water Moves Around the Planet",
  "excerpt": "Every drop of water on Earth is endlessly recycled through evaporation, clouds, rain, and rivers. Here is the water cycle explained simply, and why it shapes every landscape you see.",
  "date": "2026-06-17",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1611928482473-7b27d24eab80?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyYWluJTIwY2xvdWRzJTIwbGFuZHNjYXBlfGVufDF8MHx8fDE3ODE2ODQyMjd8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The water cycle is the continuous movement of water through evaporation, condensation, precipitation, and runoff. The same water is recycled endlessly between the oceans, the atmosphere, the land, and living things, powered by the sun.",
  "content": [
    {
      "type": "paragraph",
      "text": "The water in your glass is older than the dinosaurs. Earth has had roughly the same amount of water for billions of years; it simply keeps moving, changing form, and being used again. That endless journey is called the water cycle, and it is one of the most important ideas in all of geography because it shapes rivers, deserts, weather, and life itself."
    },
    {
      "type": "heading",
      "text": "The Big Idea"
    },
    {
      "type": "paragraph",
      "text": "The water cycle, also called the hydrological cycle, is the continuous movement of water between the oceans, the atmosphere, and the land. Water does not get used up. Instead it cycles through different states: liquid in oceans and rivers, gas in the air, and solid in ice and snow. Understanding that loop explains why it rains, why rivers keep flowing, and why some regions are lush while others are parched."
    },
    {
      "type": "heading",
      "text": "The Main Stages"
    },
    {
      "type": "paragraph",
      "text": "The cycle has no real beginning or end, but it is usually described as a sequence of steps:"
    },
    {
      "type": "list",
      "items": [
        "Evaporation: the sun heats oceans, lakes, and rivers, turning liquid water into invisible water vapor that rises into the air.",
        "Transpiration: plants release water vapor through their leaves, adding even more moisture to the atmosphere.",
        "Condensation: as vapor rises and cools, it turns back into tiny droplets that gather into clouds.",
        "Precipitation: when those droplets grow heavy enough, they fall as rain, snow, sleet, or hail.",
        "Runoff and infiltration: water flows over the surface into streams and rivers, or soaks into the ground to become groundwater.",
        "Collection: water gathers back in oceans and lakes, and the whole cycle begins again."
      ]
    },
    {
      "type": "paragraph",
      "text": "In reality these stages overlap and run all at once across the planet, with water evaporating from a warm ocean at the same moment it is freezing onto a mountain glacier thousands of miles away."
    },
    {
      "type": "heading",
      "text": "The Sun Is the Engine"
    },
    {
      "type": "paragraph",
      "text": "None of this happens without energy, and that energy comes from the sun. Sunlight drives evaporation, lifting water into the sky, while gravity pulls it back down as precipitation and sends rivers running to the sea. In a sense, every river, raincloud, and snowfield is solar-powered. The sun and gravity together keep the entire planet's water in motion, day and night, without ever stopping."
    },
    {
      "type": "heading",
      "text": "Where Earth's Water Is Stored"
    },
    {
      "type": "paragraph",
      "text": "Water spends most of its time parked in reservoirs, not in motion. The vast majority sits in the oceans as salt water. Most of the fresh water is locked up in ice caps and glaciers, with the rest held as groundwater, lakes, rivers, soil moisture, and a tiny but vital fraction in the atmosphere. A single water molecule might spend a few days as a cloud, but thousands of years frozen in an ice sheet, before rejoining the flow."
    },
    {
      "type": "heading",
      "text": "People and the Water Cycle"
    },
    {
      "type": "paragraph",
      "text": "Humans tap into the water cycle at almost every stage. We dam rivers to store runoff, pump groundwater for farming, and pipe water from wet regions to dry cities. When we drain wetlands, pave over ground, or cut down forests, we change how water soaks in, evaporates, and runs off, sometimes worsening both floods and droughts. The water cycle may be ancient and self-sustaining, but the way it plays out on any given patch of land is increasingly shaped by us."
    },
    {
      "type": "heading",
      "text": "Why the Water Cycle Shapes Geography"
    },
    {
      "type": "paragraph",
      "text": "Almost every landscape you can name is a product of the water cycle. Rivers carve valleys and build deltas. Rain and ice weather mountains down over millions of years. Where moist air is forced upward over mountains, heavy rain falls on one side while a dry rain shadow forms on the other, which is one reason many deserts sit exactly where they do. Read the water cycle, and you can start to explain the shape of the world."
    }
  ]
};
